import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import { Plus, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import {
  fetchMenuItems,
  fetchPromotions,
  deletePromotion,
  updatePromotion,
  createPromotion,
  updateMenuItem,
} from '../../api/restaurantApi';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import PromotionFormModal from './PromotionModal';
import RestaurantPromotionShimmer from './RestaurantPromotionShimmer';

export default function RestaurantPromotion({ restaurantId, propertyId, setMenuItems, loading: initialLoading }) {
  console.log('🧪 Props in RestaurantPromotion:', { restaurantId, propertyId });
  const [tab, setTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const [showFilters, setShowFilters] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    discount: '',
    start: '',
    end: '',
    status: 'active'
  });

  const [menuItems, setMenuItemsState] = useState([]);
  const [selectedMenuItemId, setSelectedMenuItemId] = useState([]);

  const [promotions, setPromotions] = useState([]);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activePromo, setActivePromo] = useState(null);

  const [editingPromoId, setEditingPromoId] = useState(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletePromoId, setDeletePromoId] = useState(null);

  const [loading, setLoading] = useState(initialLoading);

  // Delete promotion handler
  const handleDeletePromotion = async (promoId) => {
    try {
      await deletePromotion(promoId, propertyId, restaurantId);
      const updatedPromos = await fetchPromotions(restaurantId, propertyId);
      setPromotions(updatedPromos.data.promotions || []);

      const updatedMenu = await fetchMenuItems(restaurantId, propertyId);
      const cleanedMenu = (updatedMenu.data?.menu || []).map(item => ({
        ...item,
        promotion: typeof item.promotion === 'object' ? item.promotion : null
      }));

      setMenuItemsState(cleanedMenu);
      if (typeof setMenuItems === 'function') {
        setMenuItems(cleanedMenu);
      }
    } catch (err) {
      console.error('❌ Failed to delete promotion:', err);
    }
  };

  useEffect(() => {
    if (!restaurantId || !propertyId) return;
    setLoading(true);
    async function loadData() {
      try {
        const [menuRes, promoRes] = await Promise.all([
          fetchMenuItems(restaurantId, propertyId),
          fetchPromotions(restaurantId, propertyId)
        ]);
        setMenuItemsState(menuRes.data?.menu || []);
        setPromotions(promoRes.data.promotions || []);
      } catch (err) {
        console.error('❌ Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [restaurantId, propertyId]);

  // Hoisted declaration so it can be used before in hooks
  function computeStatus(promo) {
    // Normalize to one of: 'active', 'upcoming', 'expired', 'inactive'
    const now = new Date();
    const start = promo.startDate ? new Date(promo.startDate) : null;
    const end = promo.endDate ? new Date(promo.endDate) : null;
    if (promo.status === 'inactive') return 'inactive';
    if (start && end) {
      if (now < start) return 'upcoming';
      if (now > end) return 'expired';
      return 'active';
    }
    // Fallback to stored status if dates are missing
    return promo.status === 'active' ? 'active' : 'inactive';
  }

  const tabCounts = React.useMemo(() => {
    const counts = { all: promotions.length, active: 0, upcoming: 0, expired: 0, inactive: 0 };
    promotions.forEach((p) => {
      const stat = computeStatus(p);
      if (stat === 'active') counts.active += 1;
      else if (stat === 'upcoming') counts.upcoming += 1;
      else if (stat === 'expired') counts.expired += 1;
      else counts.inactive += 1;
    });
    return counts;
  }, [promotions]);

  if (loading) {
    return <RestaurantPromotionShimmer />;
  }

  return (
    <Box sx={{ px: 4, pt: 0 }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Tabs value={tab} onChange={(e, val) => setTab(val)}>
          <Tab label={`All (${tabCounts.all})`} />
          <Tab label={`Ongoing (${tabCounts.active})`} />
          <Tab label={`Upcoming (${tabCounts.upcoming})`} />
          <Tab label={`Expired (${tabCounts.expired})`} />
          <Tab label={`Inactive (${tabCounts.inactive})`} />
        </Tabs>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={() => setOpenModal(true)}
        >
          Promotion
        </Button>
      </Box>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose}>Mass Add</MenuItem>
        <MenuItem onClick={handleMenuClose}>Mass Add History</MenuItem>
      </Menu>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Promotion Title</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Discount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>End</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {promotions
            .filter(promo => {
              const status = computeStatus(promo);
              if (tab === 1) return status === 'active';
              if (tab === 2) return status === 'upcoming';
              if (tab === 3) return status === 'expired';
              if (tab === 4) return status === 'inactive';
              return true; // tab === 0, All
            })
            .map((promo) => (
            <TableRow key={promo._id}>
              <TableCell>{promo.title}</TableCell>
              <TableCell>
                {(() => {
                  const itemNames = promo.menuItems
                    ?.map(id => menuItems.find(m => m._id === id)?.name || 'Unnamed') || [];
                  const preview = itemNames.slice(0, 5).join(', ');
                  const remainingCount = itemNames.length - 5;
                  const fullText = itemNames.join(', ');

                  return (
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 300, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                          {fullText}
                        </Box>
                      }
                      arrow
                      placement="top-start"
                      enterTouchDelay={0}
                    >
                      <Box component="span" sx={{ cursor: 'pointer' }}>
                        {remainingCount > 0 ? `${preview} … +${remainingCount} more` : preview}
                      </Box>
                    </Tooltip>
                  );
                })()}
              </TableCell>
              <TableCell>{promo.discount}%</TableCell>
              <TableCell>
                {(() => {
                  const computedStatus = computeStatus(promo);
                  return (
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#fff',
                        bgcolor:
                          computedStatus === 'active'
                            ? 'green'
                            : computedStatus === 'inactive'
                            ? 'grey'
                            : computedStatus === 'expired'
                            ? 'error.main'
                            : computedStatus === 'upcoming'
                            ? 'primary.main'
                            : 'text.secondary'
                      }}
                    >
                      {computedStatus.charAt(0).toUpperCase() + computedStatus.slice(1)}
                    </Box>
                  );
                })()}
              </TableCell>
              <TableCell>{new Date(promo.startDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</TableCell>
              <TableCell>{new Date(promo.endDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</TableCell>
              <TableCell>
                <IconButton
                  onClick={(e) => {
                    setMenuAnchor(e.currentTarget);
                    setActivePromo(promo);
                  }}
                >
                  <MoreVertical size={16} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => {
              setMenuAnchor(null);
              setActivePromo(null);
            }}
            PaperProps={{
              sx: {
                minWidth: 160,
                py: 0.5,
                boxShadow: 3,
                borderRadius: 2
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                if (!activePromo) return;
                setForm({
                  title: activePromo.title,
                  description: activePromo.description,
                  discount: activePromo.discount,
                  start: activePromo.startDate?.substring(0, 10),
                  end: activePromo.endDate?.substring(0, 10),
                  status: activePromo.status
                });
                setSelectedMenuItemId(activePromo.menuItems || []);
                setEditingPromoId(activePromo._id);
                setOpenModal(true);
              }}
              sx={{ gap: 1.5, py: 1.25 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Pencil size={18} />
                <Typography variant="body2">Edit</Typography>
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                if (activePromo?._id) {
                  setDeletePromoId(activePromo._id);
                  setConfirmDeleteOpen(true);
                }
              }}
              sx={{ gap: 1.5, py: 1.25 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Trash2 size={18} />
                <Typography variant="body2">Delete</Typography>
              </Box>
            </MenuItem>
          </Menu>
      </Table>

      <PromotionFormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        form={form}
        setForm={setForm}
        menuItems={menuItems}
        selectedMenuItemId={selectedMenuItemId}
        setSelectedMenuItemId={setSelectedMenuItemId}
        onSubmit={async () => {
          setLoading(true);
          try {
            const discount = parseFloat(form.discount);
            if (isNaN(discount)) {
              alert('⚠️ Please enter a valid number for discount.');
              return;
            }

            const payload = {
              title: form.title,
              description: form.description,
              discount,
              status: form.status,
              startDate: form.start,
              endDate: form.end,
              menuItems: selectedMenuItemId
            };

            if (editingPromoId) {
              const prevRes = await fetchPromotions(restaurantId, propertyId);
              const prev = prevRes.data.promotions.find(p => p._id === editingPromoId);
              const oldIds = prev?.menuItems || [];

              await updatePromotion(editingPromoId, payload, restaurantId, propertyId);

              await Promise.all([
                ...oldIds.filter(id => !selectedMenuItemId.includes(id)).map(id =>
                  updateMenuItem(restaurantId, id, { promotion: null }, propertyId)
                ),
                ...selectedMenuItemId.map(id =>
                  updateMenuItem(restaurantId, id, { promotion: editingPromoId }, propertyId)
                )
              ]);
            } else {
              const res = await createPromotion(payload, restaurantId, propertyId);
              const promotionId = res.data?.promotion?._id;
              if (!promotionId) throw new Error('Promotion creation failed or returned no _id');

              await Promise.all(selectedMenuItemId.map(itemId =>
                updateMenuItem(restaurantId, itemId, { promotion: promotionId }, propertyId)
              ));
            }

            setEditingPromoId(null);
            setOpenModal(false);

            const promoRes = await fetchPromotions(restaurantId, propertyId);
            setPromotions(promoRes.data.promotions || []);
            const menuRes = await fetchMenuItems(restaurantId, propertyId);
            setMenuItemsState(menuRes.data?.menu || []);
            if (typeof setMenuItems === 'function') {
              setMenuItems(menuRes.data?.menu || []);
            }
          } catch (err) {
            console.error('❌ Failed to create or update promotion:', err);
          } finally {
            setLoading(false);
          }
        }}
        loading={loading}
      />
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete Promotion"
        description="Are you sure you want to delete this promotion?"
        onConfirm={() => {
          if (deletePromoId) handleDeletePromotion(deletePromoId);
          setConfirmDeleteOpen(false);
          setDeletePromoId(null);
        }}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setDeletePromoId(null);
        }}
      />
    </Box>
  );
}
